import { useEffect, useState } from 'react';
import axios from 'axios';

/**
 * Last rates for a form: a customer's last rate per product (kind 'customers', keyed by product id), or a vendor's per
 * item and service (kind 'vendors': { items, services }). Empty until a party is chosen; a party with no history, or a
 * role that can't read rates, simply gets none (the form works as before).
 *
 * @param {'customers'|'vendors'} kind
 * @param {string|number|null} partyId
 */
export default function useLastRates(kind, partyId) {
  const [rates, setRates] = useState({});
  useEffect(() => {
    setRates({});
    if (!partyId) return undefined;
    let current = true;
    axios.get(`/api/last-rates/${kind}/${partyId}`)
      .then((r) => { if (current) setRates(r.data || {}); })
      .catch(() => {});
    return () => { current = false; };
  }, [kind, partyId]);
  return rates;
}

/**
 * A vendor's rates for several vendors at once (a purchase order names a vendor per line): vendor id to { items, services }.
 */
export function useVendorRates(vendorIds) {
  const [byVendor, setByVendor] = useState({});
  const key = [...new Set((vendorIds || []).filter(Boolean).map(String))].sort().join(',');
  useEffect(() => {
    let current = true;
    key.split(',').filter(Boolean).forEach((id) => {
      if (byVendor[id]) return;
      axios.get(`/api/last-rates/vendors/${id}`)
        .then((r) => { if (current) setByVendor((prev) => ({ ...prev, [id]: r.data || {} })); })
        .catch(() => { if (current) setByVendor((prev) => ({ ...prev, [id]: {} })); });
    });
    return () => { current = false; };
    // byVendor is a cache; fetching again when it changes would loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return byVendor;
}
